import { useCallback, useState } from 'react'

import { IImportTableRow, IUserToImportData } from '../types'

interface ICsvRow {
  email?: string
  department?: string
  location?: string
  notify?: string
  birthday?: string
}

interface ICsvImportResult {
  matched: IImportTableRow[]
}

interface IUseCsvImportOptions {
  notImportedUsers: IUserToImportData[]
  existingRowIds: string[]
  departments: { id: string; name: string }[]
  locations: { id: string; name: string }[]
  defaultDepartmentId: string
  defaultLocationId: string
}

interface IUseCsvImportResult {
  isOpen: boolean
  openModal: () => void
  closeModal: () => void
  processCsvData: (data: ICsvRow[]) => ICsvImportResult
}

export const useCsvImport = ({ notImportedUsers, existingRowIds, departments, locations, defaultDepartmentId, defaultLocationId }: IUseCsvImportOptions): IUseCsvImportResult => {
  const [isOpen, setIsOpen] = useState(false)

  const openModal = useCallback(() => setIsOpen(true), [])
  const closeModal = useCallback(() => setIsOpen(false), [])

  const matchDepartment = useCallback(
    (name?: string): string => {
      if (!name) return defaultDepartmentId
      const found = departments.find((d) => d.name.toLowerCase() === name.trim().toLowerCase())
      return found ? found.id : defaultDepartmentId
    },
    [departments, defaultDepartmentId]
  )

  const matchLocation = useCallback(
    (name?: string): string => {
      if (!name) return defaultLocationId
      const found = locations.find((l) => l.name.toLowerCase() === name.trim().toLowerCase())
      return found ? found.id : defaultLocationId
    },
    [locations, defaultLocationId]
  )

  const processCsvData = useCallback(
    (data: ICsvRow[]): ICsvImportResult => {
      const matched: IImportTableRow[] = []

      for (const row of data) {
        const email = row.email?.trim().toLowerCase()
        if (!email) continue

        // Find in platform directory users
        let foundUser: { id: string; name: string; realName?: string; mail?: string } | null = null
        for (const notImported of notImportedUsers) {
          const parsed = JSON.parse(notImported.value)
          const userEmail = (parsed.mail || parsed.name || '').toLowerCase()
          if (userEmail === email) {
            foundUser = parsed
            break
          }
        }

        if (!foundUser) {
          continue
        }

        // Skip if already in the table
        if (existingRowIds.includes(foundUser.id)) {
          continue
        }

        const notify = row.notify?.trim().toLowerCase()
        const sendWelcomeMessage = notify === 'false' || notify === 'no' || notify === '0' ? false : true

        matched.push({
          id: foundUser.id,
          name: foundUser.realName || foundUser.name,
          email: foundUser.mail || email,
          departmentId: matchDepartment(row.department),
          locationId: matchLocation(row.location),
          sendWelcomeMessage,
          birthday: row.birthday,
        })
      }

      return { matched }
    },
    [notImportedUsers, existingRowIds, matchDepartment, matchLocation]
  )

  return {
    isOpen,
    openModal,
    closeModal,
    processCsvData,
  }
}
